import {
    CheckCircle,
    ChevronRight,
    DeleteOutlined,
    Error as ErrorIcon,
    Info,
    PersonOutlined,
    Warning,
} from '@mui/icons-material';
import { alpha, Chip, Divider, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { formatDistanceToNow } from 'date-fns';

import useTranslate from 'hooks/useTranslate';
import type { NotificationInterface } from 'types';
import { toTitleCase } from 'utils/v2/text';

interface NotificationCardProps {
    notification: NotificationInterface;
    onNotificationClick: (notification: NotificationInterface) => void;
    onDelete: (notification: NotificationInterface) => void;
    isDeleting?: boolean;
    showDivider?: boolean;
}

const getTypeIcon = (type: string | null) => {
    switch (type) {
        case 'SUCCESS':
            return <CheckCircle color="success" sx={{ fontSize: 18 }} />;
        case 'WARNING':
            return <Warning color="warning" sx={{ fontSize: 18 }} />;
        case 'ERROR':
            return <ErrorIcon color="error" sx={{ fontSize: 18 }} />;
        default:
            return <Info color="info" sx={{ fontSize: 18 }} />;
    }
};

const getTypeColor = (
    type: string | null
): 'success' | 'warning' | 'error' | 'info' | 'default' => {
    switch (type) {
        case 'SUCCESS':
            return 'success';
        case 'WARNING':
            return 'warning';
        case 'ERROR':
            return 'error';
        case 'INFO':
            return 'info';
        default:
            return 'default';
    }
};

const getIconPaletteKey = (type: string | null): 'success' | 'warning' | 'error' | 'info' => {
    switch (type) {
        case 'SUCCESS':
            return 'success';
        case 'WARNING':
            return 'warning';
        case 'ERROR':
            return 'error';
        default:
            return 'info';
    }
};

const getClientName = (n: NotificationInterface) => {
    const name = `${n.firstname ?? ''} ${n.lastname ?? ''}`.trim();
    return name || null;
};

export const NotificationCard = ({
    notification,
    onNotificationClick,
    onDelete,
    isDeleting = false,
    showDivider = true,
}: NotificationCardProps) => {
    const { translate } = useTranslate();
    const clientName = getClientName(notification);
    const timeAgo = formatDistanceToNow(new Date(notification.created_at), { addSuffix: true });
    const isClickable = Boolean(notification.url);
    const paletteKey = getIconPaletteKey(notification.notification_type);

    return (
        <>
            <Stack
                direction="row"
                spacing={{ xs: 1.25, sm: 1.5 }}
                onClick={() => onNotificationClick(notification)}
                role={isClickable ? 'button' : undefined}
                sx={{
                    alignItems: 'flex-start',
                    px: { xs: 1.5, sm: 2 },
                    py: { xs: 1.25, sm: 1.5 },
                    cursor: isClickable ? 'pointer' : 'default',
                    bgcolor: 'transparent',
                    transition: 'background-color 0.15s',
                    opacity: isDeleting ? 0.55 : 1,
                    '&:hover': isClickable ? { bgcolor: 'action.hover' } : undefined,
                    '&:active': isClickable ? { bgcolor: 'action.selected' } : undefined,
                }}
            >
                <Stack
                    sx={(theme) => ({
                        alignItems: 'center',
                        justifyContent: 'center',
                        mt: 0.15,
                        flexShrink: 0,
                        width: { xs: 34, sm: 38 },
                        height: { xs: 34, sm: 38 },
                        borderRadius: '50%',
                        bgcolor: alpha(theme.palette[paletteKey].main, 0.12),
                    })}
                >
                    {getTypeIcon(notification.notification_type)}
                </Stack>

                <Stack sx={{ flex: 1, minWidth: 0 }}>
                    {clientName && (
                        <Stack
                            direction="row"
                            spacing={0.5}
                            sx={{ alignItems: 'center', mb: 0.35, minWidth: 0 }}
                        >
                            <PersonOutlined
                                sx={{ fontSize: 14, color: 'text.secondary', flexShrink: 0 }}
                            />
                            <Typography
                                variant="caption"
                                sx={{
                                    fontWeight: 600,
                                    color: 'text.secondary',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {clientName}
                            </Typography>
                        </Stack>
                    )}

                    <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-start' }}>
                        <Typography
                            variant="body2"
                            sx={{
                                flex: 1,
                                fontWeight: 500,
                                lineHeight: 1.45,
                                color: 'text.primary',
                                wordBreak: 'break-word',
                            }}
                        >
                            {notification.message}
                        </Typography>
                        <Typography
                            variant="caption"
                            sx={{
                                display: { xs: 'none', sm: 'block' },
                                flexShrink: 0,
                                color: 'text.disabled',
                                whiteSpace: 'nowrap',
                                fontSize: '0.7rem',
                                pt: 0.15,
                            }}
                        >
                            {timeAgo}
                        </Typography>
                        {isClickable && (
                            <ChevronRight
                                sx={{
                                    display: { xs: 'none', sm: 'block' },
                                    fontSize: 18,
                                    color: 'text.disabled',
                                    flexShrink: 0,
                                    mt: 0.15,
                                }}
                            />
                        )}
                    </Stack>

                    <Stack
                        direction="row"
                        useFlexGap
                        spacing={0.75}
                        sx={{
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            mt: 0.75,
                        }}
                    >
                        {notification.notification_type && (
                            <Chip
                                label={toTitleCase(notification.notification_type)}
                                size="small"
                                color={getTypeColor(notification.notification_type)}
                                variant="outlined"
                                sx={{ height: 22, fontSize: '0.675rem', fontWeight: 500 }}
                            />
                        )}
                        {notification.program_name && (
                            <Chip
                                label={notification.program_name}
                                size="small"
                                variant="outlined"
                                sx={{ height: 22, fontSize: '0.675rem' }}
                            />
                        )}
                        {notification.client_status && (
                            <Chip
                                label={toTitleCase(notification.client_status)}
                                size="small"
                                sx={{ height: 22, fontSize: '0.675rem' }}
                            />
                        )}
                        <Typography
                            variant="caption"
                            sx={{
                                display: { xs: 'block', sm: 'none' },
                                color: 'text.disabled',
                                fontSize: '0.7rem',
                                ml: 'auto',
                            }}
                        >
                            {timeAgo}
                        </Typography>
                    </Stack>
                </Stack>

                <Tooltip title={translate('deleteNotification')}>
                    <IconButton
                        size="small"
                        aria-label={translate('deleteNotification')}
                        disabled={isDeleting}
                        onClick={(e) => {
                            e.stopPropagation();
                            onDelete(notification);
                        }}
                        sx={(theme) => ({
                            flexShrink: 0,
                            mt: 0.15,
                            color: 'text.secondary',
                            '&:hover': {
                                color: 'error.main',
                                bgcolor: alpha(theme.palette.error.main, 0.1),
                            },
                        })}
                    >
                        <DeleteOutlined sx={{ fontSize: 18 }} />
                    </IconButton>
                </Tooltip>
            </Stack>
            {showDivider && <Divider sx={{ mx: { xs: 1.5, sm: 2 } }} />}
        </>
    );
};

export default NotificationCard;
